import React, { useState, useEffect, useCallback } from "react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { ParentReportContent } from "./ParentReportContent";
import { apiService } from "../../services/api";
import { exportToPDF, printPage } from "../../utils/pdfExport";
import toast from "react-hot-toast";
import {
  DocumentArrowDownIcon,
  PrinterIcon,
} from "@heroicons/react/24/outline";

const ParentReportModal = ({
  isOpen,
  onClose,
  evalId,
  playerId,
  playerName,
}) => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [exporting, setExporting] = useState(false);

  const loadReport = useCallback(async () => {
    if (!evalId || !playerId) return;

    setLoading(true);
    try {
      const data = await apiService.getParentReport(evalId, playerId);
      setReportData(data);
      if (data.categories) {
        setExpandedCategories(new Set(data.categories.map((c) => c.id)));
      }
    } catch (error) {
      console.error("Error loading parent report:", error);
      toast.error("Failed to load report");
    }
    setLoading(false);
  }, [evalId, playerId]);

  useEffect(() => {
    if (isOpen) {
      loadReport();
    }
  }, [isOpen, loadReport]);

  const toggleCategory = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const filename = `${reportData.player.name.replace(/\s+/g, "-")}-Parent-Report`;
      await exportToPDF("parent-report-content", filename);
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to export PDF");
    }
    setExporting(false);
  };

  const handlePrint = () => {
    printPage();
  };

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Parent Report" size="xl">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600"></div>
        </div>
      </Modal>
    );
  }

  if (!reportData) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Parent Report" size="xl">
        <div className="text-center py-12">
          <p className="text-gray-500">Failed to load report data.</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Parent Report" size="xl">
      {/* Action buttons */}
      <div className="flex justify-end space-x-2 mb-4 print:hidden">
        <Button variant="outline" onClick={handlePrint}>
          <PrinterIcon className="w-4 h-4 mr-2" />
          Print
        </Button>
        <Button onClick={handleExportPDF} disabled={exporting}>
          <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
          {exporting ? "Exporting..." : "Download PDF"}
        </Button>
      </div>

      <ParentReportContent
        reportData={reportData}
        expandedCategories={expandedCategories}
        onToggleCategory={toggleCategory}
      />
    </Modal>
  );
};

export { ParentReportModal };
